import React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { generatePath, Link as RouterLink } from 'react-router-dom';
import CheckSharp from '@mui/icons-material/CheckSharp';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import IconButton from '@mui/material/IconButton';
import classnames from 'classnames';
import isBefore from 'date-fns/isBefore';
import PropTypes from 'prop-types';
import { any } from 'rambdax';
import Avatar from '@/components/common/Avatar';
import LastInteraction from '@/components/common/LastInteraction';
import ShowForViews from '@/components/common/ShowForViews';
import ConstituentType from '@/constants/ConstituentType';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import UserViewEnums from '@/constants/users/UserViewEnums';
import translate from '@/lib/intl/translate';
import ConstituentPropType from '@/lib/types/ConstituentPropType';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { ConstituentIconTiles } from '@/modules/ConstituentDetails';
import getInitials from '@/utils/getInitials';

function LifecycleCard({
  className,
  columnId,
  constituent,
  index,
  isDragDisabled,
  onMenuClick,
  selectedItems,
  setExcludedItems,
  setSelectedItems,
}) {
  const initials = React.useMemo(
    () =>
      getInitials(
        constituent.displayName,
        constituent.constituentType === ConstituentType.PERSON,
      ),
    [constituent],
  );

  const [hasMouseover, setHasMouseover] = React.useState(false);

  const handleMenuClick = (e) => {
    e.preventDefault();
    onMenuClick({ target: e.target, constituent, columnId });
  };

  const hasOverdueTasks = (tasks) =>
    any((task) => isBefore(new Date(task.dueDate), new Date()), tasks);

  const selected = React.useMemo(() => {
    return selectedItems.some((item) => item === constituent.constituentId);
  }, [selectedItems]);

  const handleUnselect = () => {
    setSelectedItems((previousState) => {
      const updatedItems = previousState.filter(
        (item) => item !== constituent.constituentId,
      );
      updatedItems.length = previousState.length - 1;
      return updatedItems;
    });
    setExcludedItems((previousState) => {
      return [...previousState, constituent.constituentId];
    });
  };

  const handleSelect = () => {
    setSelectedItems([...selectedItems, constituent.constituentId]);
  };

  React.useEffect(() => {
    if (!selected) {
      setHasMouseover(false);
    }
  }, [selected]);

  return (
    <Draggable
      isDragDisabled={isDragDisabled}
      key={constituent.constituentId}
      draggableId={constituent.constituentId}
      index={index}
    >
      {(provided, snapshot) => (
        <div
          className={classnames(
            className,
            'flex items-start rounded-lg border bg-white p-3 hover:border-text-secondary hover:shadow-md',
            {
              'border-grey-300': !snapshot.isDragging,
              'border-primary-dark': snapshot.isDragging,
              'border-primary-main bg-primary-main/5': selected,
            },
          )}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onMouseEnter={() => {
            if (!selected) {
              setHasMouseover(true);
            }
          }}
          onMouseLeave={() => {
            if (!selected) {
              setHasMouseover(false);
            }
          }}
        >
          {selected && (
            <CheckSharp
              className="h-10 w-10 cursor-pointer rounded-full bg-primary-main p-2 text-error-contrastText"
              onClick={handleUnselect}
            />
          )}
          {hasMouseover && !selected && (
            <CheckSharp
              className="h-10 w-10 cursor-pointer rounded-full border border-primary-main p-2 text-grey-900"
              onClick={handleSelect}
            />
          )}
          {!selected && !hasMouseover && (
            <Avatar src={constituent.avatar}>{initials}</Avatar>
          )}
          <div className="ml-2 flex-grow overflow-hidden">
            <RouterLink
              to={generatePath(AuthRoutes.CONSTITUENT, {
                constituentId: constituent.constituentId,
              })}
            >
              <div className="overflow-hidden overflow-ellipsis text-primary-main type-body2">
                {constituent.displayName}
              </div>
            </RouterLink>
            <LastInteraction date={constituent.lastInteractionDate} />
            <ConstituentIconTiles constituent={constituent} className="mt-2" />
            {constituent?.incompleteTasks?.length ? (
              <RouterLink
                to={generatePath(AuthRoutes.CONSTITUENT_TASKS, {
                  constituentId: constituent.constituentId,
                })}
                className={classnames(
                  'text-xs font-normal lowercase text-primary-main',
                  {
                    'text-error-main': hasOverdueTasks(
                      constituent.incompleteTasks,
                    ),
                  },
                )}
              >
                {translate('TASKS_COUNT', {
                  count: constituent.incompleteTasks.length,
                })}
              </RouterLink>
            ) : null}
          </div>
          <ShowForViews showFor={[UserViewEnums.ADMIN, UserViewEnums.EDITOR]}>
            <div className="-my-3 -mr-3 flex flex-col self-stretch">
              <IconButton
                type="button"
                className="mt-auto text-text-secondary"
                onClick={handleMenuClick}
                size="large"
              >
                <MoreHoriz />
              </IconButton>
            </div>
          </ShowForViews>
        </div>
      )}
    </Draggable>
  );
}

LifecycleCard.propTypes = {
  className: PropTypes.string,
  columnId: PropTypes.string.isRequired,
  constituent: ConstituentPropType.isRequired,
  index: PropTypes.number,
  isDragDisabled: PropTypes.bool,
  onMenuClick: PropTypes.func,
  selectedItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  setExcludedItems: PropTypes.func.isRequired,
  setSelectedItems: PropTypes.func.isRequired,
};

LifecycleCard.defaultProps = {
  className: '',
  index: 0,
  isDragDisabled: true,
  onMenuClick: () => {},
};

export default LifecycleCard;
